import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const TYPES = [
  'Crane',
  'Lift',
  'Welding_Machine',
  'Generator',
  'Excavator',
  'Forklift',
  'Vehicle',
  'Other',
];
const STATUSES = ['Idle', 'Working', 'Maintenance'];

type Props = { projectId?: string };

export const EquipmentPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const equipmentQ = useQuery({
    queryKey: ['cep-equipment', projectId],
    queryFn: () => epcService.getCepEquipment(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createEquipment({ ...values, projectId }),
    onSuccess: () => {
      message.success('Equipment allocated');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-equipment'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      epcService.patchEquipment(id, { status }),
    onSuccess: () => {
      message.success('Equipment status updated');
      void qc.invalidateQueries({ queryKey: ['cep-equipment'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for equipment allocations</Tag>;

  const rows = (equipmentQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Allocate equipment
        </Button>
        <Tag color="geekblue">CEP Wave 3</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={equipmentQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Type', dataIndex: 'equipment_type', width: 130 },
          { title: 'Code', dataIndex: 'equipment_code', width: 100 },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 140,
            render: (v: string, r: any) => (
              <Select
                size="small"
                style={{ width: 130 }}
                value={v}
                options={STATUSES.map((s) => ({ value: s, label: s }))}
                onChange={(status) => patchMut.mutate({ id: String(r.id), status })}
              />
            ),
          },
          { title: 'Operator', dataIndex: 'operator_name', width: 120 },
          { title: 'Hours', dataIndex: 'hours_used', width: 80 },
          { title: 'Date', dataIndex: 'allocation_date', width: 110 },
        ]}
      />
      <Modal
        title="Allocate equipment"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{
            status: 'Idle',
            allocationDate: new Date().toISOString().slice(0, 10),
          }}
        >
          <Form.Item name="equipmentType" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="equipmentCode" label="Asset / code">
            <Input />
          </Form.Item>
          <Form.Item name="packageId" label="Execution package">
            <Select
              allowClear
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="operatorName" label="Operator">
            <Input />
          </Form.Item>
          <Form.Item name="hoursUsed" label="Hours used">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="allocationDate" label="Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EquipmentPanel;
